import React from "react";
import { motion } from "framer-motion";
import { Container } from "../ui";
import { cn } from "../../utils/cn";

export interface FeaturesProps {
  className?: string;
}

const featuresList = [
  {
    icon: "/icon-local-discovery-transparent.png",
    title: "LOCAL DISCOVERY",
    titleColor: "text-[#6D28D9]",
    description: "Explore nearby shops, services, hours and reviews.",
  },
  {
    icon: "/icon-offers-fast-transparent.png",
    title: "OFFERS THAT MOVE FAST",
    titleColor: "text-[#0D9488]",
    description: "Deals by the hour, day, week and month.",
  },
  {
    icon: "/icon-loyalty-coins-transparent.png",
    title: "LOYALTY COINS",
    titleColor: "text-[#D97706]",
    description: "Earn coins. Rise from Silver to Gold.",
  },
  {
    icon: "/icon-rewards-vouchers-transparent.png",
    title: "REWARDS & VOUCHERS",
    titleColor: "text-[#7C3AED]",
    description: "Trade coins for coupons and vouchers.",
  },
];

const Features: React.FC<FeaturesProps> = ({ className }) => {
  return (
    <section
      id="how-it-works"
      className={cn(
        "relative bg-white py-16 sm:py-20 lg:py-28 overflow-hidden",
        className
      )}
    >
      {/* Soft lavender ambience so the glass rows have something to frost */}
      <div className="absolute inset-0 bg-dot-grid pointer-events-none" aria-hidden="true" />
      <div className="absolute -top-24 -left-24 w-[420px] h-[420px] bg-purple-300/25 rounded-full blur-3xl pointer-events-none" aria-hidden="true" />
      <div className="absolute bottom-0 right-0 w-[460px] h-[380px] bg-amber-200/25 rounded-full blur-3xl pointer-events-none" aria-hidden="true" />

      <Container size="xl" padding="md" className="relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          {/* Left Column: 3D Shopping Illustration */}
          <motion.div
            className="lg:col-span-6 flex justify-center items-center"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <div className="relative w-full max-w-[420px] sm:max-w-[480px] lg:max-w-[540px]">
              <img
                src="/illustration.png"
                alt="SetGo - One app. Four ways to make every visit count"
                className="w-full h-auto object-contain drop-shadow-2xl select-none"
              />
            </div>
          </motion.div>

          {/* Right Column: Headline & 4 Feature Items */}
          <motion.div
            className="lg:col-span-6 text-center lg:text-left"
            initial={{ opacity: 0, y: 25 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl sm:text-4xl lg:text-[44px] font-extrabold text-[#0F172A] tracking-tight leading-[1.15] mb-8 sm:mb-10">
              One app. <span className="text-[#7C3AED]">Four ways</span> to
              <br className="hidden sm:inline" /> make every visit count.
            </h2>

            <div className="space-y-3 sm:space-y-4 max-w-lg mx-auto lg:mx-0">
              {featuresList.map((item, index) => (
                <motion.div
                  key={item.title}
                  className="glass-light flex items-start gap-4 sm:gap-5 text-left rounded-2xl p-3.5 sm:p-4 transition-shadow duration-300 hover:shadow-[0_18px_40px_-16px_rgba(76,29,149,0.28)]"
                  whileHover={{ y: -2 }}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.45, delay: index * 0.1 }}
                >
                  {/* Icon */}
                  <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center rounded-xl bg-gradient-to-br from-white to-purple-50 border border-purple-100/80 shadow-[inset_0_1px_0_#fff,0_4px_10px_-4px_rgba(76,29,149,0.2)]">
                    <img
                      src={item.icon}
                      alt={item.title}
                      className="w-9 h-9 object-contain drop-shadow-sm select-none"
                    />
                  </div>

                  {/* Text */}
                  <div>
                    <h3
                      className={cn(
                        "text-xs sm:text-[13px] font-black uppercase tracking-wider mb-1",
                        item.titleColor
                      )}
                    >
                      {item.title}
                    </h3>
                    <p className="text-slate-600 text-sm sm:text-base leading-relaxed font-normal">
                      {item.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </Container>
    </section>
  );
};

export { Features };
export default Features;
